import type { BaseEntity, ID, Timestamp } from './common.types';
import type { TaxCalcMode } from './document-base.types';

export type CustomerRank = 'SSS' | 'SS' | 'S' | 'A' | 'B' | 'C';
export type CustomerStatus = 'active' | 'inactive' | 'prospect';

/** ランクの決定方法。auto=取引実績から自動算定、manual=手動固定（自動再計算の対象外）。 */
export type RankMode = 'auto' | 'manual';

/** ランク自動算定の根拠となる集計値（月次締め時にキャッシュ更新）。 */
export interface CustomerRankStats {
  lifetimeInvoiced: number; // 累計請求確定額
  lifetimeOrderCount: number; // 累計受注件数
  rolling12mInvoiced: number; // 直近12ヶ月の請求確定額（休眠判定用）
}

/**
 * 取引先の区分。1つの取引先が複数の役割を兼ねられる（複合）。
 * - customer: 顧客（案件を発注してくれる取引先）
 * - supplier: 仕入先（仕入発注の相手先）
 * - subcontractor: 外注先（外注発注の相手先）
 * 例: 外注先でありながら案件をもらえば customer も付与され取引先となる。
 */
export type PartnerType = 'customer' | 'supplier' | 'subcontractor';

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  customer: '顧客',
  supplier: '仕入先',
  subcontractor: '外注先',
};

export interface Customer extends BaseEntity {
  code: string;
  name: string;
  nameKana: string;
  rank: CustomerRank;
  /** ランクの決定方法（既定 auto）。manual の顧客は自動再計算の対象外。 */
  rankMode: RankMode;
  /** ランクを自動算定した最終日時（手動のみは null）。 */
  rankUpdatedAt: Timestamp | null;
  /** 自動算定の根拠となる集計値のキャッシュ（月次締め時に更新）。 */
  rankStats: CustomerRankStats | null;
  status: CustomerStatus;
  partnerTypes: PartnerType[];
  industry: string;
  postalCode: string;
  prefecture: string;
  phone: string;
  fax: string | null;
  email: string | null;
  website: string | null;
  assignedUserId: ID | null;
  paymentTerms: number;
  notes: string | null;

  // B1 追加フィールド（Phase 5-B）
  parentId: ID | null; // 本社/部署（自己参照）
  areaId: string | null; // エリア（請求先/divisioncode）
  limitTypeId: ID | null; // 締め日マスタ参照
  paymentDateId: ID | null; // 支払日マスタ参照
  taxCalcMode: TaxCalcMode; // 税計算モード（document-base.typesから）
  tel2: string | null; // 電話番号2
  group: string | null; // グループ
  // 住所の共通仕様（AIRS統一）: 〒(postalCode) / 都道府県(prefecture) /
  // 住所（市区町村）/ 住所（番地）/ 住所（建物名・フロア）/ 部署
  addr1: string; // 住所（市区町村）
  addr2: string; // 住所（番地）
  addr3: string; // 住所（建物名・フロア）
  department: string; // 部署
}

export interface CustomerContact extends BaseEntity {
  customerId: ID;
  name: string;
  nameKana: string;
  department: string | null;
  position: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  isPrimary: boolean;
}
